import { isFullvisioSlotType } from '@omecare/utils';
import { useTranslation } from 'react-i18next';
import { SelectedDatesCard } from '@/components/sheets/slot-sheet/selected-dates-card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { OfficeDoctorAttendingItem } from '@/hooks/queries/use-get-office';
import { useAuthorization } from '@/hooks/use-authorization';
import { DatePickerForm } from '../datepicker/datepicker-form';
import { RequiredLabel } from '../required-label';
import { useAddOfficeWorkScheduleSlotFormViewModel } from './add-office-work-schedule-slot-form.viewmodel';
import { AddOfficeWorkScheduleSlotUseForm } from './use-add-office-work-schedule-slot-form';

type Props = {
  form: AddOfficeWorkScheduleSlotUseForm;
  doctorAttendings: OfficeDoctorAttendingItem[];
  showDoctorAttendingSelector: boolean;
};

const AddOfficeWorkScheduleSlotForm = ({
  form,
  doctorAttendings,
  showDoctorAttendingSelector,
}: Props) => {
  const { t } = useTranslation();

  const viewModel = useAddOfficeWorkScheduleSlotFormViewModel({
    form,
    doctorAttendings,
  });

  // Remote (fullvisio) slots are always "sans assistant·e", so the manual SA
  // toggle is forced on and disabled when Fullvisio is selected.
  const isFullvisio = isFullvisioSlotType(form.watch('slotType'));

  // Pinning a slot "sans assistant·e" is a staff-level decision: attending
  // doctors can create their own slots but cannot set the SA override.
  const { canEditWorkSchedules } = useAuthorization();
  const canForceWithoutAssistant = canEditWorkSchedules();

  return (
    <Form {...form}>
      <form className="grid gap-5">
        {showDoctorAttendingSelector && (
          <FormField
            control={form.control}
            name="doctorAttendingId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <RequiredLabel>
                    {t(
                      'add-office-work-schedule-slot-form.doctor-attending.label',
                    )}
                  </RequiredLabel>
                </FormLabel>

                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="" />
                    </SelectTrigger>
                  </FormControl>

                  <SelectContent side="bottom">
                    {viewModel.selectOptions.doctorAttendingOptions.map(
                      (item) => (
                        <SelectItem
                          key={item.id}
                          value={item.value}
                        >
                          {t(item.label)}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>

                <FormDescription>
                  {t(
                    'add-office-work-schedule-slot-form.doctor-attending.description',
                  )}
                </FormDescription>

                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {/* Period of the day */}
        <FormField
          control={form.control}
          name="periodOfTheDay"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t('add-office-work-schedule-slot-form.period-of-day.label')}
              </FormLabel>

              <Select
                onValueChange={field.onChange}
                defaultValue={field.value}
              >
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="" />
                  </SelectTrigger>
                </FormControl>

                <SelectContent side="bottom">
                  {viewModel.selectOptions.periodOfTheDayOptions.map((item) => (
                    <SelectItem
                      key={item.id}
                      value={item.id}
                    >
                      {t(item.label)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <FormMessage />
            </FormItem>
          )}
        />

        {/* Consultation mode: présentiel / fullvisio */}
        <FormField
          control={form.control}
          name="slotType"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t('add-office-work-schedule-slot-form.slot-type.label')}
              </FormLabel>

              <Select
                onValueChange={field.onChange}
                defaultValue={field.value}
              >
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="" />
                  </SelectTrigger>
                </FormControl>

                <SelectContent side="bottom">
                  {viewModel.selectOptions.slotTypeOptions.map((item) => (
                    <SelectItem
                      key={item.id}
                      value={item.value}
                    >
                      {t(item.label)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <FormDescription>
                {t('add-office-work-schedule-slot-form.slot-type.description')}
              </FormDescription>

              <FormMessage />
            </FormItem>
          )}
        />

        {/* Force without assistant */}
        <FormField
          control={form.control}
          name="forceWithoutAssistant"
          render={({ field }) => (
            <FormItem>
              <div className="flex flex-row items-start gap-3 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value || isFullvisio}
                    disabled={isFullvisio || !canForceWithoutAssistant}
                    onCheckedChange={(checked) =>
                      field.onChange(checked === true)
                    }
                  />
                </FormControl>

                <div className="grid gap-1 leading-none">
                  <FormLabel className="cursor-pointer font-normal">
                    {t(
                      'add-office-work-schedule-slot-form.force-without-assistant.label',
                    )}
                  </FormLabel>

                  <FormDescription>
                    {t(
                      'add-office-work-schedule-slot-form.force-without-assistant.description',
                    )}
                  </FormDescription>
                </div>
              </div>

              <FormMessage />
            </FormItem>
          )}
        />

        {/* Dates the slots will be planned on */}
        <DatePickerForm
          form={form}
          fieldName="specificDates"
          captionLayout="dropdown"
          inline={false}
          label={t('add-office-work-schedule-slot-form.specific-dates.label')}
          description={t(
            'add-office-work-schedule-slot-form.specific-dates.description',
          )}
          placeholder={t(
            'add-office-work-schedule-slot-form.specific-dates.placeholder',
          )}
          mode="multiple"
        />

        <SelectedDatesCard
          dates={viewModel.specificSlots.selectedDates}
          periodLabel={viewModel.specificSlots.periodSummaryLabel}
          onRemove={viewModel.api.removeSelectedDate}
          onClear={viewModel.api.clearSelectedDates}
        />
      </form>
    </Form>
  );
};

export default AddOfficeWorkScheduleSlotForm;
